import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, API } from "../lib/api";
import { Download, ArrowRight, Copy, Check, Mail, FileSearch, X as XIcon } from "lucide-react";

const DIMENSIONS = [
  ["Entity Recognition",        "entity_recognition",      true],
  ["AI Discovery Presence",     "category_inclusion",      true],
  ["Competitive Positioning",   "competitive_association", true],
  ["Accuracy & Consistency",    "accuracy_consistency",    false],
  ["Authority & Confidence",    "authority_signals",       false],
];

const PLATFORM_LABELS = [
  ["chatgpt",    "ChatGPT"],
  ["gemini",     "Gemini"],
  ["claude",     "Claude"],
  ["perplexity", "Perplexity"],
];

function dimTotal(value) {
  if (value && typeof value === "object") {
    if (typeof value.total === "number") return Math.max(0, Math.min(20, value.total));
    return Math.max(0, Math.min(20,
      (value.chatgpt || 0) + (value.gemini || 0) + (value.claude || 0) + (value.perplexity || 0)
    ));
  }
  const n = parseInt(value, 10);
  return isNaN(n) ? 0 : Math.max(0, Math.min(20, n));
}

const PIPELINE_OPTIONS = [
  { key: "new",        label: "New",         dot: "#8a7e6d" },
  { key: "contacted",  label: "Contacted",   dot: "#d4a04a" },
  { key: "replied",    label: "Replied",     dot: "#b8963e" },
  { key: "audit_sent", label: "Audit Sent",  dot: "#6db66d" },
  { key: "closed",     label: "Closed",      dot: "#5e5448" },
];

function SectionRule() {
  return <div className="my-7 sm:my-9" style={{ background: "var(--paper-rule)", height: 1 }} />;
}

function SectionLabel({ children }) {
  return (
    <p
      className="font-mono-eav font-bold text-[10px] tracking-[0.25em] uppercase"
      style={{ color: "#8e7126" }}
    >
      {children}
    </p>
  );
}

function CopyButton({ text, label = "Copy", testId }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked */ }
  };
  return (
    <button
      onClick={handleCopy}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 font-mono-eav text-[11px] tracking-wider uppercase px-2.5 py-1 rounded-sm transition-colors"
      style={{
        background: copied ? "rgba(109, 182, 109, 0.15)" : "rgba(184, 150, 62, 0.12)",
        color: copied ? "#3f8a3f" : "#8e7126",
        border: `1px solid ${copied ? "#6db66d" : "#b8963e"}`,
      }}
    >
      {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> {label}</>}
    </button>
  );
}

export default function Snapshot() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [pipelineStatus, setPipelineStatus] = useState("new");
  const [pipelineSaving, setPipelineSaving] = useState(false);
  const [evidence, setEvidence] = useState(null); // {dimension, ...} | null
  const [evidenceLoading, setEvidenceLoading] = useState(false);

  const openEvidence = async (dimensionKey) => {
    setEvidenceLoading(true);
    setEvidence({ dimension: dimensionKey, loading: true });
    try {
      const { data } = await api.get(`/audits/${id}/evidence/${dimensionKey}`);
      setEvidence(data);
    } catch (e) {
      setEvidence({ dimension: dimensionKey, error: e?.response?.data?.detail || "Could not load evidence" });
    } finally {
      setEvidenceLoading(false);
    }
  };
  const closeEvidence = () => setEvidence(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/audits/${id}`);
        setData(data);
        setPipelineStatus(data?.audit?.pipeline_status || "new");
        if (data?.audit?.status !== "complete") {
          navigate(`/processing/${id}`, { replace: true });
        }
      } catch {
        setErr("Could not load snapshot");
      }
    })();
  }, [id, navigate]);

  const updatePipeline = async (next) => {
    setPipelineStatus(next);
    setPipelineSaving(true);
    try {
      await api.patch(`/audits/${id}/pipeline`, { pipeline_status: next });
    } catch { /* clipboard blocked */ }
    setPipelineSaving(false);
  };

  if (err) return <div className="max-w-3xl mx-auto p-12 text-[#c46a5a]" data-testid="snapshot-error">{err}</div>;
  if (!data || !data.snapshot) {
    return (
      <div className="max-w-3xl mx-auto p-12 text-[#c9bca4] font-mono-eav text-sm" data-testid="snapshot-loading">
        Loading snapshot…
      </div>
    );
  }

  const { audit, snapshot } = data;
  const tier = snapshot.tier_label || "";
  const total = snapshot.total_score ?? 0;
  const outreach = snapshot.cold_outreach || {};
  const outreachSubject = outreach.subject || `AI visibility check — ${audit.company_name}`;
  const outreachBody = outreach.body || "";
  const fullEmailText = `Subject: ${outreachSubject}\n\n${outreachBody}`;

  const handlePdfDownload = async () => {
    try {
      const token = localStorage.getItem("eav_token");
      const res = await fetch(`${API}/audits/${id}/pdf`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${audit.company_name.replace(/\s+/g, "_")}_AI_Visibility_Snapshot.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert("PDF generation failed");
    }
  };

  const dateStr = new Date(snapshot.created_at).toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="snapshot-page">
      <Helmet>
        <title>{snapshot?.company_name ? `${snapshot.company_name} — AI Visibility Snapshot · The Entity Agency` : "AI Visibility Snapshot · The Entity Agency"}</title>
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      {/* Top action bar (dark) */}
      <div className="flex flex-wrap justify-between gap-3 mb-6">
        <Link to="/dashboard" data-testid="snapshot-back-link"
              className="text-sm text-[#8a7e6d] hover:text-[#b8963e] transition-colors font-mono-eav tracking-wider">
          ← BACK TO DASHBOARD
        </Link>
        <div className="flex items-center gap-2">
          <button onClick={handlePdfDownload} data-testid="download-pdf-button" className="btn-gold text-sm py-2">
            <Download className="w-4 h-4" /> Download PDF
          </button>
        </div>
      </div>

      {/* The PAPER document */}
      <article
        className="paper rounded-md shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7),0_8px_24px_rgba(0,0,0,0.4)] overflow-hidden"
        data-testid="snapshot-document"
        style={{ background: "var(--paper)", color: "var(--ink-bg)" }}
      >
        <div className="px-8 sm:px-14 py-10 sm:py-14">
          <div className="h-[2px]" style={{ background: "#b8963e" }} />

          {/* Header */}
          <div className="mt-7 flex flex-wrap justify-between items-baseline gap-3">
            <p className="font-mono-eav font-bold text-[11px] tracking-[0.28em]" style={{ color: "#8e7126" }}>
              THE ENTITY AGENCY
            </p>
            <p className="font-mono-eav text-[11px] tracking-[0.2em] uppercase" style={{ color: "#5e5448" }}>
              {dateStr}
            </p>
          </div>
          <h1 className="mt-4 font-serif-display text-4xl sm:text-5xl tracking-tight"
              style={{ fontWeight: 700, color: "#1a1612" }}>
            AI Visibility Snapshot
            <sup className="text-base font-normal ml-1" style={{ color: "#8e7126" }}>™</sup>
          </h1>
          <p className="mt-2 italic font-serif-display" style={{ color: "#8a7e6d", fontSize: "0.95rem" }}>
            AI Visibility Intelligence · theentityagency.com
          </p>

          <SectionRule />

          {/* Engagement + Score */}
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <SectionLabel>Engagement</SectionLabel>
              <dl className="mt-4 space-y-2.5">
                {[
                  ["Company",  audit.company_name],
                  ["Website",  audit.website],
                  ["Industry", audit.industry],
                  ["Region",   audit.region],
                  ["Date",     dateStr],
                ].map(([k, v]) => (
                  <div key={k} className="flex gap-3 items-baseline" data-testid={`detail-${k.toLowerCase()}`}>
                    <dt className="font-mono-eav text-[10px] tracking-[0.18em] uppercase w-24 flex-shrink-0" style={{ color: "#8a7e6d" }}>
                      {k}
                    </dt>
                    <dd className="text-[15px]" style={{ color: "#1a1612" }}>
                      {k === "Website"
                        ? <a href={v} target="_blank" rel="noreferrer" className="underline decoration-[#b8963e]/40 hover:decoration-[#b8963e] break-all">{v}</a>
                        : v}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="lg:col-span-5">
              <SectionLabel>AI Visibility Score™</SectionLabel>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="font-serif-display text-[88px] leading-none"
                      style={{ fontWeight: 700, color: "#1a1612" }} data-testid="snapshot-total-score">
                  {total}
                </span>
                <span className="font-serif-display text-2xl" style={{ color: "#8a7e6d" }}>/ 100</span>
              </div>
              <p className="mt-3 font-mono-eav font-bold text-[12px] tracking-[0.22em] uppercase"
                 style={{ color: "#8e7126" }} data-testid="snapshot-tier-label">
                Tier — {tier}
              </p>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: "#5e5448" }}
                 data-testid="snapshot-tier-description">
                {snapshot.tier_description}
              </p>
            </div>
          </div>

          <SectionRule />

          {/* Score Breakdown */}
          <section data-testid="score-breakdown-section">
            <SectionLabel>Score Breakdown</SectionLabel>
            <div className="mt-5 space-y-3.5">
              {DIMENSIONS.map(([label, key, hasBreakdown]) => {
                const raw = snapshot.scores?.[key] ?? 0;
                const v = dimTotal(raw);
                const reason = snapshot.score_reasoning?.[key];
                const isObj = raw && typeof raw === "object";
                return (
                  <div key={key} data-testid={`breakdown-${key}`}>
                    <div className="flex justify-between items-baseline gap-4">
                      <span className="text-[15px]" style={{ color: "#1a1612" }}>{label}</span>
                      <div className="flex-1 mx-4 max-w-md">
                        <div className="h-[6px]" style={{ background: "var(--paper-deep)" }}>
                          <div className="h-full bar-fill"
                               style={{ background: "#b8963e", "--target": v / 20 }} />
                        </div>
                      </div>
                      <span className="font-mono-eav font-bold text-sm" style={{ color: "#1a1612" }}>
                        {v} / 20
                      </span>
                    </div>
                    {hasBreakdown && isObj && (
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 pl-4"
                           data-testid={`platform-breakdown-${key}`}>
                        <span className="font-mono-eav text-[10px]" style={{ color: "#8a7e6d" }}>└</span>
                        {PLATFORM_LABELS.map(([pk, pLabel]) => (
                          <span key={pk}
                                className="font-mono-eav text-[10px] tracking-wide"
                                style={{ color: "#8a7e6d" }}>
                            {pLabel} <strong style={{ color: "#5e5448" }}>{raw[pk] ?? 0}/5</strong>
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Surface the exact search category used for Category Inclusion */}
                    {key === "category_inclusion" && snapshot.category_phrase && (
                      <div className="mt-1 pl-4" data-testid="category-phrase-used">
                        <span className="font-mono-eav text-[10px] tracking-wide" style={{ color: "#8e7126" }}>
                          SEARCH CATEGORY USED:
                        </span>
                        <span className="ml-2 text-[12px] italic font-serif-display" style={{ color: "#5e5448" }}>
                          &ldquo;{snapshot.category_phrase}&rdquo;
                        </span>
                      </div>
                    )}
                    {reason && (
                      <div className="mt-1.5 space-y-1" data-testid={`reasoning-${key}`}>
                        {String(reason).split(/\n\n+/).map((para, idx) => (
                          <p key={idx} className="text-xs italic font-serif-display leading-relaxed" style={{ color: "#8a7e6d" }}>
                            {para.trim()}
                          </p>
                        ))}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => openEvidence(key)}
                      data-testid={`view-evidence-${key}`}
                      className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-mono-eav tracking-[0.18em] uppercase hover:underline transition-colors"
                      style={{ color: "#8e7126" }}
                    >
                      <FileSearch className="w-3 h-3" />
                      View Evidence
                    </button>
                  </div>
                );
              })}
            </div>
          </section>

          <SectionRule />

          <section data-testid="interpretation-section">
            <SectionLabel>How AI Currently Interprets This Company</SectionLabel>
            <p className="mt-4 font-serif-display text-[18px] leading-[1.55]"
               style={{ color: "#1a1612", fontWeight: 400 }} data-testid="interpretation-text">
              {snapshot.interpretation || snapshot.consensus_understanding}
            </p>
          </section>

          <SectionRule />

          <section data-testid="consensus-section">
            <SectionLabel>Consensus Summary</SectionLabel>
            <ul className="mt-4 space-y-4">
              {[
                ["AI understands this company as", snapshot.consensus_understanding],
                ["AI consistently mentions",       snapshot.consensus_competitors],
                ["AI hesitates because",            snapshot.consensus_hesitation],
              ].map(([label, text]) => (
                <li key={label} className="flex gap-3" data-testid={`consensus-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <span className="text-[#b8963e] flex-shrink-0 mt-1.5">●</span>
                  <div>
                    <p className="font-mono-eav font-bold text-[10px] tracking-[0.2em] uppercase" style={{ color: "#8a7e6d" }}>
                      {label}
                    </p>
                    <p className="mt-1 text-[15px] leading-[1.5]" style={{ color: "#1a1612" }}>{text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <SectionRule />

          <SectionRule />

          {/* ════════ AI RECOMMENDATION TEST ════════ */}
          {(() => {
            const rec = snapshot.recommendation_test;
            if (!rec || (!rec.queries && !rec.results)) return null;
            const queries = rec.queries || [];
            const results = rec.results || {};
            const rows = [
              ["Appears for direct brand queries",   "appears_for_brand_queries"],
              ["Accurately summarized",              "accurately_summarized"],
              ["Recommended as a top option",        "recommended_as_top_option"],
              ["Differentiation clearly explained",  "differentiation_clearly_explained"],
              ["Recommended over competitors",       "recommended_over_competitors"],
            ];
            return (
              <section data-testid="recommendation-test-section">
                <SectionLabel>AI Recommendation Test</SectionLabel>
                {queries.length > 0 && (
                  <div className="mt-4">
                    <p className="font-mono-eav text-[10px] tracking-[0.2em] uppercase" style={{ color: "#8a7e6d" }}>
                      Representative queries
                    </p>
                    <ul className="mt-1 space-y-1">
                      {queries.slice(0, 2).map((q, i) => (
                        <li key={i} className="text-[14px] font-serif-display italic" style={{ color: "#1a1612" }}>
                          • &ldquo;{q}&rdquo;
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <ul className="mt-4 space-y-1.5" data-testid="rec-test-rows">
                  {rows.map(([label, key]) => {
                    const val = !!results[key];
                    return (
                      <li key={key}
                          data-testid={`rec-${key}`}
                          className="flex justify-between items-center py-1.5 border-b last:border-b-0"
                          style={{ borderColor: "#ebe3d4" }}>
                        <span className="text-[15px]" style={{ color: "#1a1612" }}>{label}</span>
                        <span className="text-2xl leading-none select-none" aria-label={val ? "yes" : "no"}>
                          {val ? "✅" : "❌"}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                {rec.insight && (
                  <div className="mt-4 pl-4 border-l-2" style={{ borderColor: "#b8963e" }}>
                    <p className="font-mono-eav text-[10px] tracking-[0.2em] uppercase" style={{ color: "#8e7126" }}>
                      Insight
                    </p>
                    <p className="mt-1 text-[14px] font-serif-display italic" style={{ color: "#5e5448" }}>
                      {rec.insight}
                    </p>
                  </div>
                )}
              </section>
            );
          })()}

          {snapshot.recommendation_test && <SectionRule />}

          {/* ════════ PRIMARY FAILURE MODE (v3.0 — FIX 7) ════════ */}
          {snapshot.primary_failure_mode?.label && (
            <>
              <section data-testid="primary-failure-mode-section">
                <SectionLabel>Primary Failure Mode</SectionLabel>
                <div className="mt-4 inline-block px-3 py-1 rounded-sm font-mono-eav text-[11px] tracking-[0.2em] uppercase font-bold"
                     style={{ background: "#b8963e", color: "#0c0a08" }}
                     data-testid="primary-failure-mode-tag">
                  {snapshot.primary_failure_mode.label}
                </div>
                {snapshot.primary_failure_mode.rationale && (
                  <p className="mt-3 font-serif-display text-[15px] leading-[1.55]"
                     style={{ color: "#5e5448" }} data-testid="primary-failure-mode-rationale">
                    {snapshot.primary_failure_mode.rationale}
                  </p>
                )}
              </section>
              <SectionRule />
            </>
          )}

          {/* ════════ CATEGORY DISCOVERY — APPEARED RATE (Fix 5 + Fix 1) ════════ */}
          {(() => {
            const raw = snapshot.competition_visibility_evidence;
            const rows = Array.isArray(raw) ? raw : (raw?.rows || []);
            const excluded = Array.isArray(raw) ? [] : (raw?.hallucinated_excluded_platforms || []);
            const hasTable = rows.length > 0;
            if (!snapshot.competition_visibility && !hasTable) return null;
            return (
              <>
                <section data-testid="competition-visibility-section">
                  <SectionLabel>Competition Visibility</SectionLabel>
                  {snapshot.competition_visibility && (
                    <p className="mt-4 font-serif-display text-[16px] leading-[1.55]"
                       style={{ color: "#1a1612", fontWeight: 400 }} data-testid="competition-visibility-text">
                      {snapshot.competition_visibility}
                    </p>
                  )}
                  {hasTable && (
                    <div className="mt-5" data-testid="competition-visibility-evidence">
                      <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase mb-2"
                         style={{ color: "#8e7126" }}>
                        Category Discovery — Appeared Rate
                      </p>
                      <div className="grid grid-cols-12 gap-2 pb-2 border-b" style={{ borderColor: "#c9bfa9" }}>
                        <p className="col-span-7 font-mono-eav text-[10px] tracking-[0.2em] uppercase" style={{ color: "#8e7126" }}>Company</p>
                        <p className="col-span-3 font-mono-eav text-[10px] tracking-[0.2em] uppercase" style={{ color: "#8e7126" }}>Appeared</p>
                        <p className="col-span-2 font-mono-eav text-[10px] tracking-[0.2em] uppercase text-right" style={{ color: "#8e7126" }}>Rate</p>
                      </div>
                      {rows.slice(0, 5).map((row, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 py-2 border-b"
                             style={{ borderColor: "#ebe3d4" }}
                             data-testid={`competition-row-${i}`}>
                          <p className="col-span-7 text-[14px]"
                             style={{ color: row.is_subject ? "#8e7126" : "#1a1612",
                                      fontWeight: row.is_subject ? 700 : 400 }}>
                            {row.name}{row.is_subject ? "  ◆" : ""}
                          </p>
                          <p className="col-span-3 font-mono-eav text-[13px]" style={{ color: "#5e5448" }}>
                            {row.appeared}/{row.of}
                          </p>
                          <p className="col-span-2 font-mono-eav text-[13px] font-bold text-right"
                             style={{ color: row.is_subject ? "#8e7126" : "#1a1612" }}>
                            {row.percent}%
                          </p>
                        </div>
                      ))}
                      <p className="mt-3 text-[12px] italic leading-[1.5]"
                         style={{ color: "#7a6f5f" }}
                         data-testid="competition-visibility-note">
                        Shows how often each company appears in AI-generated category
                        lists across 4 platforms. Higher rate = stronger AI category
                        presence. Hallucinated or off-category mentions of the subject
                        are excluded.
                        {excluded.length > 0 && (
                          <>{" "}Excluded: {excluded.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(", ")}.</>
                        )}
                      </p>
                    </div>
                  )}
                </section>
                <SectionRule />
              </>
            );
          })()}

          <section data-testid="gaps-section">
            <SectionLabel>Top 3 Visibility Gaps</SectionLabel>
            <ol className="mt-4 space-y-3" data-testid="top-gaps">
              {(snapshot.top_gaps || []).slice(0, 3).map((g, i) => {
                const isObj = g && typeof g === "object";
                const name = isObj ? (g.name || "") : String(g);
                const finding = isObj ? (g.finding || "") : "";
                const impact  = isObj ? (g.impact || "")  : "";
                return (
                  <li key={i} className="flex gap-3" data-testid={`gap-${i}`}>
                    <span className="font-serif-display font-bold text-lg" style={{ color: "#8e7126" }}>{i + 1}.</span>
                    <div className="flex-1">
                      <p className="text-[15px] font-bold" style={{ color: "#1a1612" }}>{name}</p>
                      {finding && (
                        <p className="mt-1 text-[14px]" style={{ color: "#5e5448" }}>{finding}</p>
                      )}
                      {impact && (
                        <p className="mt-1 text-[13px] italic font-serif-display" style={{ color: "#8a7e6d" }}>
                          {impact}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>

          <SectionRule />

          <section data-testid="quick-wins-section">
            <SectionLabel>Quick Wins</SectionLabel>
            <ol className="mt-4 space-y-3" data-testid="quick-wins-list">
              {(snapshot.quick_wins || []).slice(0, 5).map((qw, i) => (
                <li key={i} className="flex gap-3" data-testid={`quick-win-${i}`}>
                  <span className="font-serif-display font-bold text-lg" style={{ color: "#8e7126" }}>{i + 1}.</span>
                  <div className="flex-1">
                    <p className="text-[15px] font-bold" style={{ color: "#1a1612" }}>{qw.title}</p>
                    {qw.detail && (
                      <p className="mt-1 text-[14px]" style={{ color: "#5e5448" }}>{qw.detail}</p>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <SectionRule />

          {/* $300 CTA */}
          <section data-testid="next-step-section">
            <div className="rounded-md border-2 px-6 sm:px-8 py-7"
                 style={{ borderColor: "#b8963e", background: "rgba(184, 150, 62, 0.06)" }}>
              <SectionLabel>Next Step</SectionLabel>
              <h3 className="mt-3 font-serif-display text-2xl sm:text-3xl" style={{ fontWeight: 700, color: "#1a1612" }}>
                Want the full AI Visibility Audit?
              </h3>
              <p className="mt-3 text-[15px]" style={{ color: "#5e5448" }}>
                <span className="font-serif-display font-bold text-xl" style={{ color: "#1a1612" }}>$300</span>
                {" "}— delivered in 48 hours.
                <span className="ml-3 font-mono-eav text-xs tracking-wider" style={{ color: "#8e7126" }}>
                  theentityagency.com
                </span>
              </p>
              <div className="mt-5">
                <a
                  href={`mailto:contact@theentityagency.com?subject=Full AI Visibility Audit for ${encodeURIComponent(audit.company_name)}&body=Hi,%0A%0AI'd like to book a full AI Visibility Audit for ${encodeURIComponent(audit.company_name)}.%0A%0ASnapshot score: ${total}/100 (${tier})%0A%0AThanks.`}
                  data-testid="book-audit-cta"
                  className="inline-flex items-center gap-2 font-medium px-5 py-3 rounded-sm transition-colors"
                  style={{ background: "#1a1612", color: "#f4efe6" }}
                >
                  Book the Full Audit
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </section>

          <div className="mt-10 pt-6 flex justify-between items-baseline" style={{ borderTop: "1px solid var(--paper-rule)" }}>
            <p className="font-mono-eav text-[10px] tracking-[0.2em] uppercase" style={{ color: "#8a7e6d" }}>
              © The Entity Agency · AI Visibility Snapshot™ · Confidential
            </p>
            <p className="font-mono-eav text-[10px] tracking-[0.2em] uppercase" style={{ color: "#8a7e6d" }}>
              theentityagency.com
            </p>
          </div>
        </div>
      </article>

      {/* ════════ COLD OUTREACH (off-document, dark) ════════ */}
      <section className="mt-10 eav-card p-6 sm:p-8" data-testid="outreach-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="overline">Cold outreach · auto-drafted</p>
            <h2 className="mt-3 font-serif-display text-3xl text-[#f4efe6]" style={{fontWeight: 700}}>
              Ready-to-send <em className="text-[#b8963e] italic font-normal">cold email</em>.
            </h2>
            <p className="mt-2 text-sm text-[#c9bca4] max-w-xl">
              Generated from this snapshot. Personalize as needed, copy, and send.
            </p>
          </div>
          <CopyButton
            text={fullEmailText}
            label="Copy full email"
            testId="outreach-copy-full"
          />
        </div>

        <div className="mt-7 space-y-5">
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="overline-soft">Subject</p>
              <CopyButton text={outreachSubject} label="Copy subject" testId="outreach-copy-subject" />
            </div>
            <div
              data-testid="outreach-subject"
              className="font-serif-display text-lg text-[#f4efe6] px-4 py-3 rounded-sm border"
              style={{ borderColor: "#2a2620", background: "#0a0908" }}
            >
              {outreachSubject}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="overline-soft">Body</p>
              <CopyButton text={outreachBody} label="Copy body" testId="outreach-copy-body" />
            </div>
            <div
              data-testid="outreach-body"
              className="text-[#c9bca4] leading-[1.7] text-[15px] px-4 py-4 rounded-sm border whitespace-pre-wrap"
              style={{ borderColor: "#2a2620", background: "#0a0908" }}
            >
              {outreachBody || "— Cold outreach copy not yet generated. Re-run this snapshot to regenerate."}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <a
              href={`mailto:?subject=${encodeURIComponent(outreachSubject)}&body=${encodeURIComponent(outreachBody)}`}
              data-testid="outreach-open-mailto"
              className="btn-ghost text-sm py-2"
            >
              <Mail className="w-4 h-4" /> Open in mail client
            </a>
          </div>
        </div>
      </section>

      {/* ════════ PIPELINE STATUS ════════ */}
      <section className="mt-6 eav-card p-6 sm:p-8" data-testid="pipeline-card">
        <p className="overline">Outreach pipeline</p>
        <h2 className="mt-3 font-serif-display text-2xl text-[#f4efe6]" style={{fontWeight: 700}}>
          Where is <em className="text-[#b8963e] italic font-normal">{audit.company_name}</em> in your pipeline?
        </h2>
        <div className="mt-6 flex flex-wrap gap-2">
          {PIPELINE_OPTIONS.map((opt) => {
            const active = pipelineStatus === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => updatePipeline(opt.key)}
                disabled={pipelineSaving}
                data-testid={`pipeline-set-${opt.key}`}
                className="flex items-center gap-2 px-4 py-2.5 rounded-sm border text-sm transition-all font-mono-eav tracking-wider uppercase"
                style={{
                  borderColor: active ? "#b8963e" : "#2a2620",
                  background:  active ? "rgba(184, 150, 62, 0.12)" : "transparent",
                  color:       active ? "#b8963e" : "#c9bca4",
                }}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: opt.dot }} />
                {opt.label}
              </button>
            );
          })}
        </div>
        <p className="mt-5 font-mono-eav text-[11px] tracking-wider text-[#5e5448]">
          Visible across your dashboard and pipeline view.
        </p>
      </section>

      {/* Analyst detail */}
      <details className="mt-6 group eav-card" data-testid="analyst-details">
        <summary className="cursor-pointer list-none flex items-center justify-between p-5 hover:border-[#b8963e]/40 transition-colors">
          <div>
            <p className="overline">Analyst detail</p>
            <p className="mt-1 font-serif-display font-bold text-[#f4efe6]">
              Per-model summaries · raw responses analysis
            </p>
          </div>
          <span className="text-[#8a7e6d] font-mono-eav text-xs group-open:rotate-180 transition-transform">▼</span>
        </summary>
        <div className="px-5 pb-5 grid lg:grid-cols-3 gap-3">
          {[
            { k: "openai",    n: "ChatGPT (GPT-5.2)" },
            { k: "anthropic", n: "Claude Sonnet 4.5" },
            { k: "gemini",    n: "Gemini 3 Flash" },
          ].map((p) => (
            <div key={p.k} className="eav-card p-5">
              <p className="overline-soft">{p.n}</p>
              <p className="mt-3 text-sm text-[#c9bca4] leading-relaxed" data-testid={`provider-summary-${p.k}`}>
                {snapshot.per_provider_summary?.[p.k] || "—"}
              </p>
            </div>
          ))}
        </div>
      </details>

      {/* ════════ View Evidence Modal ════════ */}
      {evidence && (
        <EvidenceModal evidence={evidence} loading={evidenceLoading} onClose={closeEvidence} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EVIDENCE MODAL — shows prompt + verbatim responses + rubric for one dimension
// ─────────────────────────────────────────────────────────────────────────────
const PLATFORM_NICE = {
  chatgpt: "ChatGPT", gemini: "Gemini",
  claude:  "Claude",  perplexity: "Perplexity",
};

function EvidenceModal({ evidence, loading, onClose }) {
  if (!evidence) return null;
  const isLoading = loading || evidence.loading;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="evidence-modal"
      className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      style={{ background: "rgba(8, 8, 7, 0.85)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-4xl my-6 rounded-md"
        style={{
          background: "var(--paper, #f4efe6)",
          border: "1px solid #b8963e",
          color: "#1a1612",
        }}
      >
        {/* Gold top strip */}
        <div style={{ background: "#b8963e", height: 6, borderTopLeftRadius: 4, borderTopRightRadius: 4 }} />

        <div className="p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: "#8e7126" }}>
                Scoring Evidence
              </p>
              <h2 className="mt-1 font-serif-display text-3xl" style={{ fontWeight: 700, color: "#1a1612" }}>
                {evidence.label || evidence.dimension}
              </h2>
              {evidence.score != null && (
                <p className="mt-1 font-mono-eav text-sm tracking-wider" style={{ color: "#5e5448" }}>
                  Score: <strong style={{ color: "#1a1612" }}>{
                    typeof evidence.score === "object"
                      ? (evidence.score.total ?? 0) + " / 20"
                      : evidence.score + " / 20"
                  }</strong>
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="evidence-modal-close"
              className="p-2 rounded-sm transition-colors"
              style={{ color: "#5e5448" }}
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>

          {evidence.category_phrase && evidence.dimension === "category_inclusion" && (
            <div className="mt-4 px-3 py-2 rounded-sm" style={{ background: "rgba(184, 150, 62, 0.10)", borderLeft: "3px solid #b8963e" }}>
              <span className="font-mono-eav text-[10px] tracking-[0.18em] uppercase" style={{ color: "#8e7126" }}>
                Search category used
              </span>
              <p className="mt-0.5 font-serif-display italic text-base" style={{ color: "#1a1612" }}>
                &ldquo;{evidence.category_phrase}&rdquo;
              </p>
            </div>
          )}

          {isLoading && (
            <p className="mt-6 font-mono-eav text-sm tracking-wider" style={{ color: "#8a7e6d" }}>
              LOADING EVIDENCE…
            </p>
          )}

          {evidence.error && (
            <p className="mt-6 text-sm" style={{ color: "#b03a2e" }}>{evidence.error}</p>
          )}

          {!isLoading && !evidence.error && (
            <>
              {/* 1. Prompts sent */}
              <section className="mt-6">
                <h3 className="font-mono-eav text-[11px] tracking-[0.22em] uppercase font-bold" style={{ color: "#8e7126" }}>
                  1 · Prompts sent to each platform
                </h3>
                <div className="mt-3 space-y-3">
                  {(evidence.prompts || []).map((p) => (
                    <div key={p.id} className="rounded-sm" style={{ border: "1px solid #c9bfa9", background: "#ebe3d4" }}>
                      <div className="px-3 py-1.5" style={{ background: "#c9bfa9" }}>
                        <span className="font-mono-eav text-[10px] tracking-[0.18em] uppercase font-bold" style={{ color: "#5e5448" }}>
                          Prompt {p.id} — {p.label}
                        </span>
                      </div>
                      <pre className="px-3 py-2 whitespace-pre-wrap text-[12px] font-serif-display" style={{ color: "#1a1612" }}>
{p.text}
                      </pre>
                    </div>
                  ))}
                </div>
              </section>

              {/* 2. Verbatim responses */}
              <section className="mt-6">
                <h3 className="font-mono-eav text-[11px] tracking-[0.22em] uppercase font-bold" style={{ color: "#8e7126" }}>
                  2 · Verbatim responses from each AI platform
                </h3>
                <div className="mt-3 space-y-3">
                  {(evidence.responses || []).map((r, i) => (
                    <details key={i} className="rounded-sm" style={{ border: "1px solid #c9bfa9", background: "#fbf8f1" }} data-testid={`evidence-response-${r.provider}-${r.prompt_id}`}>
                      <summary className="cursor-pointer px-3 py-2 flex items-center justify-between" style={{ background: "#ebe3d4" }}>
                        <span className="font-mono-eav text-[11px] tracking-[0.18em] uppercase font-bold" style={{ color: "#1a1612" }}>
                          {PLATFORM_NICE[r.provider] || r.provider} · Prompt {r.prompt_id}
                          {r.error && <span style={{ color: "#b03a2e", marginLeft: 8 }}>· ERROR</span>}
                        </span>
                        <span className="text-xs" style={{ color: "#8a7e6d" }}>click to expand</span>
                      </summary>
                      <div className="px-3 py-3 text-[12.5px] leading-[1.55] font-serif-display whitespace-pre-wrap" style={{ color: "#1a1612" }}>
                        {r.error
                          ? <span style={{ color: "#b03a2e" }}>{r.error}</span>
                          : (r.response || "(empty response)")}
                      </div>
                    </details>
                  ))}
                </div>
              </section>

              {/* 3. Rubric */}
              <section className="mt-6">
                <h3 className="font-mono-eav text-[11px] tracking-[0.22em] uppercase font-bold" style={{ color: "#8e7126" }}>
                  3 · Rubric criteria applied
                </h3>
                <table className="mt-3 w-full text-sm">
                  <tbody>
                    {(evidence.rubric_criteria || []).map(([score, desc], i) => (
                      <tr key={i} style={{ borderTop: i ? "1px solid #c9bfa9" : "none" }}>
                        <td className="py-2 pr-3 align-top font-mono-eav text-[12px] font-bold" style={{ color: "#8e7126", width: 60 }}>
                          {score}
                        </td>
                        <td className="py-2 text-[13.5px] font-serif-display" style={{ color: "#1a1612" }}>
                          {desc}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>

              {/* 4. Score reasoning */}
              {evidence.reasoning && (
                <section className="mt-6">
                  <h3 className="font-mono-eav text-[11px] tracking-[0.22em] uppercase font-bold" style={{ color: "#8e7126" }}>
                    4 · How the score was derived
                  </h3>
                  <div className="mt-3 space-y-2">
                    {String(evidence.reasoning).split(/\n\n+/).map((para, i) => (
                      <p key={i} className="text-[13.5px] font-serif-display italic leading-relaxed" style={{ color: "#5e5448" }}>
                        {para.trim()}
                      </p>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

